import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IAsset } from '../../../../interfaces/asset';
import type { IKlachten } from './KlachtenAddEditModal';
import type { ILichamelijkOnderzoek } from './LichamelijkOnderzoek';
import type { IRedenen } from './RedenenAddEditModal';
import type { IRisicofactoren } from './Risicofactoren';
import type { IVoorgeschiedenis } from './Voorgeschiedenis';
import { DesktopProcessActions } from '../../../../../../common/enums/desktopProcessActions';
import { DesktopProcessStatus } from '../../../../../../common/enums/desktopProcessStatus';
import { HardwareProcessStatus } from '../../../../../../common/enums/hardwareProcessStatus';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import {
    Button,
    FormAnamnesisButtons,
    FormOverlayMessage,
    FormParagraph,
    HeaderDivider,
    NullableInputText,
    SecondHeader,
    ThirdHeader,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { openKsyosUrl, OpenKsyosUrlResult } from '../../../../elements/openKsyosUrl';
import { ProcedureAssetThumbnails, RequestAssetThumbnails } from '../../../episodes/common';
import { desktopProcessErrorCodeText } from '../../TCOR/v1/desktopProcessErrorCodeText';
import { AnamneseType } from './AnamneseType';
import { CardioTypes } from './cardioTypes';
import { KlachtenAddEdit } from './Klachten';
import { LichamelijkOnderzoekAddEditModal } from './LichamelijkOnderzoek';
import { RedenenAddEdit } from './Redenen';
import { RisicofactorenAddEditModal } from './Risicofactoren';
import { VoorgeschiedenisAddEditModal } from './Voorgeschiedenis';

interface IRustEcgAddEditProps {
    afterStartDesktop: () => Promise<void>;
    cardioType: CardioTypes;
    customControlData: ICustomControlData;
    employmentId: string;
    errors: ValidationErrors;
    handleChange: (customControl: Partial<ICustomControlData>) => Promise<void>;
    isDirectProcedureAdded?: boolean;
    organizationId: string;
    patientId: string;
    previousCustomControlData?: ICustomControlData;
    procedureId?: string;
    requestId?: string;
    saveAsDraft: () => Promise<string | undefined>;
    serviceConsumer?: {
        usingRestEcgMeter?: boolean;
    };
    serviceId: string;
    serviceProvider?: {
        usingRestEcgMeter?: boolean;
    };
    setIsProcessing: (isProcessing: boolean) => Promise<void>;
    startDesktop: boolean;
    validateOne: (name: keyof ICustomControlData) => Promise<void>;
}

interface IImportedRestEcgData {
    afgerondOp: string | null;
    commentaar: string | null;
    HR: number | null;
    interpretatie: string | null;
    P: number | null;
    P_HOEK: number | null;
    PQ: number | null;
    QRS: number | null;
    QRS_HOEK: number | null;
    QT: number | null;
    QTc: number | null;
    T_HOEK: number | null;
}

export interface ICustomControlData {
    conclusie_diagnose: ConclusieDiagnoseOption;
    desktopProcessId?: string;
    files: IAsset[];
    importedRestEcgData: IImportedRestEcgData | null;
    klacht: string | null;
    klachten: IKlachten | null;
    lab: string | null;
    lichamelijkOnderzoek: ILichamelijkOnderzoek | null;
    reden: string | null;
    redenen: IRedenen | null;
    risicofactoren: IRisicofactoren | null;
    voorgeschiedenis: IVoorgeschiedenis | null;
}

interface IRustEcgAddEditState {
    fileUploadErrorMessage?: string;
    hasDesktopProcessTimedOut: boolean;
    isMissingKsyosUrlHandler: boolean;
    procedureId?: string;
    processError?: string;
    processStatus?: HardwareProcessStatus;

    requestId?: string;
    show: AnamneseType | null;
}

const timeoutIntervalSeconds = 2;
const timeoutIntervalMilliseconds = timeoutIntervalSeconds * 1000;
const maxWaitTimeSeconds = 30;

export class RustEcgAddEdit extends Component<IRustEcgAddEditProps, IRustEcgAddEditState> {
    private desktopProcessUpdateInterval: number;

    constructor(props: IRustEcgAddEditProps) {
        super(props);
        this.state = {
            isMissingKsyosUrlHandler: false,
            hasDesktopProcessTimedOut: false,
            show: null,
        };
    }

    public async componentDidMount() {
        await this.getDesktopProcessStatus();
    }

    public async componentWillUnmount() {
        if (this.desktopProcessUpdateInterval) {
            clearInterval(this.desktopProcessUpdateInterval);
        }
    }

    public async componentDidUpdate() {
        if (this.props.startDesktop) {
            await this.props.afterStartDesktop();
            await this.startRestEcg();
        }
    }

    public render() {
        return (
            <>
                {((this.props.serviceConsumer && this.props.serviceConsumer.usingRestEcgMeter) ||
                    (this.props.serviceProvider && this.props.serviceProvider.usingRestEcgMeter)) && (
                    <>
                        <FormGroup>
                            <FormParagraph>
                                {this.state.isMissingKsyosUrlHandler && (
                                    <span>
                                        Download de nieuwe Ksyos cardiologie koppeling{' '}
                                        <a
                                            href={`/desktop?organizationId=${this.props.organizationId}`}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            hier
                                        </a>
                                        .
                                    </span>
                                )}
                                {!this.restEcgStatus() && (
                                    <>
                                        {' '}
                                        Heb je de nieuwe cardiologie koppeling al geïnstalleerd? Klik{' '}
                                        <a href={`/desktop?organizationId=${this.props.organizationId}`}>hier</a> om te
                                        downloaden.
                                    </>
                                )}
                            </FormParagraph>
                            {this.restEcgStatus()}
                        </FormGroup>

                        {this.isDesktopProcessRunning() && (
                            <FormOverlayMessage>
                                {this.state.processStatus === HardwareProcessStatus.starting && (
                                    <SecondHeader text="Het onderzoek is gestart" />
                                )}
                                {this.state.processStatus === HardwareProcessStatus.running && (
                                    <SecondHeader text="Het onderzoek is bezig" />
                                )}
                                {this.state.processStatus === HardwareProcessStatus.uploading && (
                                    <SecondHeader text="Het onderzoek wordt geüpload" />
                                )}

                                <Button
                                    color="primary"
                                    onClick={async () => await this.cancelDesktopProcess()}
                                >
                                    Annuleer import
                                </Button>

                                {this.state.hasDesktopProcessTimedOut && (
                                    <ThirdHeader text="Sorry, we kunnen geen verbinding maken met de elektrocardiograaf. Probeer het opnieuw of neem contact op met de Servicedesk dan zoeken we het voor je uit" />
                                )}
                            </FormOverlayMessage>
                        )}
                    </>
                )}

                {this.props.cardioType === CardioTypes.restEcgRequest &&
                    (this.state.requestId || this.props.requestId) &&
                    this.props.customControlData &&
                    this.props.customControlData.files &&
                    this.props.customControlData.files.length > 0 && (
                        <FormGroup>
                            <FormLabel>{translateKey('request.attachment')}</FormLabel>
                            <RequestAssetThumbnails
                                assets={this.props.customControlData.files as IAsset[]}
                                requestId={this.state.requestId ? this.state.requestId : this.props.requestId!}
                            />
                        </FormGroup>
                    )}

                {this.props.cardioType === CardioTypes.restEcgProcedure &&
                    (this.state.procedureId || this.props.procedureId) &&
                    this.props.customControlData &&
                    this.props.customControlData.files &&
                    this.props.customControlData.files.length > 0 && (
                        <FormGroup>
                            <FormLabel>{translateKey('request.attachment')}</FormLabel>
                            <ProcedureAssetThumbnails
                                assets={this.props.customControlData.files as IAsset[]}
                                procedureId={this.state.procedureId ? this.state.procedureId : this.props.procedureId!}
                            />
                        </FormGroup>
                    )}

                {(this.props.serviceConsumer || (this.props.serviceProvider && this.props.isDirectProcedureAdded)) &&
                    this.renderAnamnese()}
            </>
        );
    }

    private async handleChange<K extends keyof ICustomControlData>(name: K, value: ICustomControlData[K]) {
        await this.props.handleChange({ [name]: value });
    }

    private async setAnamneseData(
        typeOfAnamnese: AnamneseType,
        item?: IKlachten | IRedenen | IVoorgeschiedenis | IRisicofactoren | ILichamelijkOnderzoek
    ) {
        await this.props.handleChange({ [typeOfAnamnese]: { ...item } });
        await this.setState({ show: null });
    }

    private async deleteAnamneseData(typeOfAnamnese: AnamneseType) {
        await this.props.handleChange({ [typeOfAnamnese]: null });
        await this.setState({ show: null });
    }

    private isKlachtenRequired() {
        return !(
            this.props.previousCustomControlData &&
            (this.props.previousCustomControlData.klacht || this.props.previousCustomControlData.klachten)
        );
    }

    private isRedenenRequired() {
        return !(
            this.props.previousCustomControlData &&
            (this.props.previousCustomControlData.reden || this.props.previousCustomControlData.redenen)
        );
    }

    private restEcgStatus() {
        switch (this.state.processStatus) {
            case HardwareProcessStatus.starting:
                return 'Het onderzoek is gestart';
            case HardwareProcessStatus.running:
                return 'Het onderzoek is bezig';
            case HardwareProcessStatus.uploading:
                return 'Het onderzoek wordt geüpload';
            case HardwareProcessStatus.success:
                if (
                    this.props.customControlData.importedRestEcgData &&
                    this.props.customControlData.importedRestEcgData.afgerondOp
                ) {
                    return `Het onderzoek is afgerond op ${DateTime.from(
                        this.props.customControlData.importedRestEcgData.afgerondOp
                    ).format('DD-MM-YYYY')}`;
                }

                return 'Het onderzoek is klaar';
            case HardwareProcessStatus.failed:
                if (this.state.processError) {
                    const errorCode = this.state.processError;

                    return (
                        <Alert appearance="warning">
                            Sorry, we hebben het onderzoek niet kunnen afronden.{' '}
                            {desktopProcessErrorCodeText[errorCode]}
                        </Alert>
                    );
                }

                return (
                    <Alert appearance="warning">
                        Sorry, we hebben het onderzoek niet kunnen afronden. Probeer het opnieuw of neem contact op met
                        de Servicedesk dan zoeken we het voor je uit
                    </Alert>
                );
            default:
                if (this.state.processError) {
                    return <Alert appearance="warning">{this.state.processError}</Alert>;
                }
                break;
        }
    }

    private isDesktopProcessRunning() {
        return (
            this.state.processStatus === HardwareProcessStatus.running ||
            this.state.processStatus === HardwareProcessStatus.starting ||
            this.state.processStatus === HardwareProcessStatus.uploading
        );
    }

    private async startRestEcg() {
        if (this.isDesktopProcessRunning()) {
            return;
        }

        await this.props.setIsProcessing(true);

        await this.setState({
            processStatus: HardwareProcessStatus.starting,
            processError: undefined,
            hasDesktopProcessTimedOut: false,
        });
        await this.props.handleChange({ importedRestEcgData: null, desktopProcessId: undefined });

        if (this.desktopProcessUpdateInterval) {
            clearInterval(this.desktopProcessUpdateInterval);
        }

        const requestId = await this.props.saveAsDraft();
        if (!requestId) {
            await this.setState({ processStatus: undefined });

            return this.props.setIsProcessing(false);
        }

        const desktopAction =
            this.props.cardioType === CardioTypes.restEcgProcedure
                ? DesktopProcessActions.restEcg_procedure_cardioPerfect
                : DesktopProcessActions.restEcg_request_cardioPerfect;

        let procedureId = this.props.procedureId || this.state.procedureId;
        if (!procedureId && desktopAction === DesktopProcessActions.restEcg_procedure_cardioPerfect) {
            const procedure = await fetchJson(`/api/requests/${requestId}/procedure`, 'GET');
            procedureId = procedure.json.id;
        }

        await this.setState({ requestId, procedureId });

        const desktopProcess = await fetchJson('/api/desktopProcesses', 'POST', {
            serviceId: this.props.serviceId,
            action: desktopAction,
            requestId: requestId,
        });

        const patient = await fetchJson(`/api/patients/${this.props.patientId}`, 'GET');
        const params = {
            employmentId: this.props.employmentId,
            desktopProcessId: desktopProcess.json.id,
            token: desktopProcess.json.token,
            apiUrl: location.protocol + '//' + location.host,
            gender: patient.json.gender,
        };

        await this.props.handleChange({ desktopProcessId: desktopProcess.json.id });

        const url = 'ksyos://restecg/cardioperfect?encoded=' + btoa(JSON.stringify(params));

        const openResult = await openKsyosUrl(url);

        if (openResult === OpenKsyosUrlResult.noHandler) {
            await this.setState({ processStatus: undefined, isMissingKsyosUrlHandler: true });
            await this.props.setIsProcessing(false);
        } else {
            await this.desktopStatusInterval(desktopAction, requestId, procedureId);
        }
    }

    private async desktopStatusInterval(
        desktopProcessAction: DesktopProcessActions,
        requestId: string,
        procedureId?: string
    ) {
        let timeout = 0;
        this.desktopProcessUpdateInterval = window.setInterval(async () => {
            const url = `/api/desktopProcesses/${this.props.customControlData.desktopProcessId}`;
            const result = await fetchJson(url, 'GET');

            const desktopProcess: { data?: { error?: string }; status: DesktopProcessStatus } = result.json;

            if (desktopProcess.status === DesktopProcessStatus.starting) {
                if (timeout >= maxWaitTimeSeconds) {
                    await this.setState({
                        hasDesktopProcessTimedOut: true,
                        processStatus: HardwareProcessStatus.failed,
                    });
                    clearInterval(this.desktopProcessUpdateInterval);
                    await this.props.setIsProcessing(false);
                } else {
                    await this.setState({ processStatus: HardwareProcessStatus.starting });
                    timeout += timeoutIntervalSeconds;
                }
            } else if (desktopProcess.status === DesktopProcessStatus.running) {
                await this.setState({ processStatus: HardwareProcessStatus.running });
            } else if (desktopProcess.status === DesktopProcessStatus.uploading) {
                await this.setState({ processStatus: HardwareProcessStatus.uploading });
            } else if (desktopProcess.status === DesktopProcessStatus.completed) {
                if (desktopProcess.data && desktopProcess.data.error) {
                    await this.setState({
                        processStatus: HardwareProcessStatus.failed,
                        processError: desktopProcess.data.error,
                    });
                } else {
                    if (desktopProcessAction === DesktopProcessActions.restEcg_request_cardioPerfect) {
                        const requestResult = await fetchJson(`/api/requests/${requestId}`, 'GET');
                        await this.props.handleChange(requestResult.json.customControlData);
                    } else if (
                        procedureId &&
                        desktopProcessAction === DesktopProcessActions.restEcg_procedure_cardioPerfect
                    ) {
                        const procedureResult = await fetchJson(`/api/procedures/${procedureId}`, 'GET');
                        await this.props.handleChange(procedureResult.json.customControlData);
                    }

                    await this.setState({ processStatus: HardwareProcessStatus.completed });
                }

                clearInterval(this.desktopProcessUpdateInterval);
                await this.props.setIsProcessing(false);
            }

            timeout += timeoutIntervalSeconds;
        }, timeoutIntervalMilliseconds);
    }

    private async getDesktopProcessStatus() {
        if (this.props.customControlData.desktopProcessId && this.props.requestId) {
            const url = `/api/desktopProcesses/${this.props.customControlData.desktopProcessId}`;
            const desktopProcess = await fetchJson(url, 'GET');

            if (
                desktopProcess.json.status === DesktopProcessStatus.starting ||
                desktopProcess.json.status === DesktopProcessStatus.running ||
                desktopProcess.json.status === DesktopProcessStatus.uploading
            ) {
                await this.props.setIsProcessing(true);
                await this.desktopStatusInterval(
                    desktopProcess.json.action,
                    this.props.requestId,
                    this.props.procedureId
                );
            }
        }
    }

    private async cancelDesktopProcess() {
        if (this.desktopProcessUpdateInterval) {
            clearInterval(this.desktopProcessUpdateInterval);
        }

        await this.setState({ processStatus: undefined, processError: undefined });
        await this.props.handleChange({ importedRestEcgData: null, desktopProcessId: undefined });
        await this.props.setIsProcessing(false);
    }

    private renderAnamnese() {
        return (
            <>
                <HeaderDivider text="Anamnese" />
                <KlachtenAddEdit
                    customControlData={this.props.customControlData}
                    errors={this.props.errors}
                    handleChange={this.props.handleChange}
                    isRequired={this.isKlachtenRequired()}
                    validateOne={this.props.validateOne}
                />
                <RedenenAddEdit
                    customControlData={this.props.customControlData}
                    errors={this.props.errors}
                    handleChange={this.props.handleChange}
                    isRequired={this.isRedenenRequired()}
                    validateOne={this.props.validateOne}
                />

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.lab')}>
                    <FormLabel>Lab</FormLabel>
                    <InputContainer>
                        <NullableInputText
                            error={this.props.errors.get('customControlData.lab')}
                            onChange={async (value) => await this.handleChange('lab', value)}
                            onDoValidate={async () => await this.props.validateOne('lab')}
                            placeholder={translateKey('request.fillLabValues')}
                            value={this.props.customControlData.lab}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup>
                    <FormLabel>Meer informatie toevoegen</FormLabel>
                    <InputContainer>
                        <FormAnamnesisButtons
                            buttons={[
                                <CheckMarkButton
                                    isChecked={!!this.props.customControlData.voorgeschiedenis}
                                    onClick={async () => await this.setState({ show: AnamneseType.voorgeschiedenis })}
                                >
                                    Voorgeschiedenis
                                </CheckMarkButton>,
                                <CheckMarkButton
                                    isChecked={!!this.props.customControlData.risicofactoren}
                                    onClick={async () => await this.setState({ show: AnamneseType.risicofactoren })}
                                >
                                    Risicofactoren
                                </CheckMarkButton>,
                                <CheckMarkButton
                                    isChecked={!!this.props.customControlData.lichamelijkOnderzoek}
                                    onClick={async () =>
                                        await this.setState({ show: AnamneseType.lichamelijkOnderzoek })
                                    }
                                >
                                    Lichamelijk onderzoek
                                </CheckMarkButton>,
                            ]}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                {this.state.show === AnamneseType.voorgeschiedenis && (
                    <VoorgeschiedenisAddEditModal
                        anamneseData={this.props.customControlData.voorgeschiedenis}
                        deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.voorgeschiedenis)}
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (voorgeschiedenis) =>
                            await this.setAnamneseData(AnamneseType.voorgeschiedenis, voorgeschiedenis)
                        }
                    />
                )}

                {this.state.show === AnamneseType.risicofactoren && (
                    <RisicofactorenAddEditModal
                        anamneseData={this.props.customControlData.risicofactoren}
                        deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.risicofactoren)}
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (risicofactor) =>
                            await this.setAnamneseData(AnamneseType.risicofactoren, risicofactor)
                        }
                    />
                )}

                {this.state.show === AnamneseType.lichamelijkOnderzoek && (
                    <LichamelijkOnderzoekAddEditModal
                        anamneseData={this.props.customControlData.lichamelijkOnderzoek}
                        deleteAnamneseData={async () =>
                            await this.deleteAnamneseData(AnamneseType.lichamelijkOnderzoek)
                        }
                        onClose={async () => await this.setState({ show: null })}
                        setAnamneseData={async (onderzoek) =>
                            await this.setAnamneseData(AnamneseType.lichamelijkOnderzoek, onderzoek)
                        }
                    />
                )}
            </>
        );
    }
}

export function initializeCustomControlDataRustEcg() {
    return {
        voorgeschiedenis: null,
        risicofactoren: null,
        lichamelijkOnderzoek: null,
        klachten: null,
        klacht: null,
        redenen: null,
        reden: null,
        lab: null,
        files: [],
        importedRestEcgData: null,
        previousCustomControlData: null,
        conclusie_diagnose: null,
    };
}

export function tcorV1InitializeCustomControlDataRustEcg(isDirectProcedureAdded?: boolean) {
    return isDirectProcedureAdded ? initializeCustomControlDataRustEcg() : { files: [] };
}
